import { MouseEvent, ReactNode, RefObject, useRef } from 'react';
import styles from './health100-footer.module.scss';

interface DialogProps {
  dialogRef: RefObject<HTMLDialogElement>;
  isOpen: boolean;
  title: ReactNode;
  body: ReactNode;
  size: 'large' | 'medium';
}

const Dialog = ({ dialogRef, title, body, size }: DialogProps) => {
  const handleClose = () => {
    dialogRef.current?.close();
  };

  return (
    <dialog ref={dialogRef} className={styles.dialog}>
      <div className={`${styles.dialogContainer} ${size === 'medium' ? styles.dialogSizeMedium : ''}`}>
        <div className={styles.dialogHeader}>
          <h2 className={styles.dialogTitle}>{title}</h2>
          <button type="button" className={styles.dialogClose} aria-label="Close dialog" onClick={handleClose}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6 6 18"></path>
              <path d="m6 6 12 12"></path>
            </svg>
          </button>
        </div>
        <div className={styles.dialogBody}>{body}</div>
      </div>
    </dialog>
  );
};

const Health100Footer = () => {
  const aboutDialogRef = useRef<HTMLDialogElement>(null);
  const supportDialogRef = useRef<HTMLDialogElement>(null);

  const handleOpenDialog = (ev: MouseEvent<HTMLAnchorElement>) => {
    ev.preventDefault();
    const linkHref = (ev.target as HTMLAnchorElement).href;
    if (linkHref.endsWith('#about')) {
      aboutDialogRef.current?.showModal();
    } else if (linkHref.endsWith('#support')) {
      supportDialogRef.current?.showModal();
    }
  };

  return (
    <>
      <footer className={styles.footer}>
        <p className={styles.copyright}>© 2026 Health100. All rights reserved.</p>
        <nav className={styles.nav}>
          <a className={styles.navLink} href="#about" onClick={handleOpenDialog}>
            About
          </a>
          <a className={styles.navLink} href="/privacy_policy">
            Privacy Policy
          </a>
          <a className={styles.navLink} href="/phr_privacy_policy">
            PHR Privacy &amp; Security Notice
          </a>
          <a className={styles.navLink} href="/Terms_of_use">
            Terms of Use
          </a>
          <a className={styles.navLink} href="#support" onClick={handleOpenDialog}>
            Support
          </a>
        </nav>
      </footer>
      <Dialog
        dialogRef={aboutDialogRef}
        isOpen={true}
        title="About Health100"
        size="large"
        body={
          <>
            <p>You&apos;ve got big moments ahead, and we&apos;re here to help you stay ready for them.</p>
            <p>
              Through meaningful engagement and intelligent insights, Health100 helps you stay close to your health so
              that you can focus on your goals and not the interruptions.
            </p>
            <p>
              We break the mold of traditional health care experiences that leave your information fragmented, and your
              care disconnected. We&apos;re building an open engagement platform that works across your entire health
              ecosystem to simplify your care.
            </p>
            <p>
              An integrated and unified approach means that we bring together pharmacies, care providers, health
              insurance, and health data points from all over, into one secure platform.
            </p>
            <p>
              Coming this spring is a true end-to-end care experience, so you can focus on what matters most, your
              health.
            </p>
          </>
        }
      />
      <Dialog
        dialogRef={supportDialogRef}
        size="medium"
        isOpen={false}
        title="Support"
        body={
          <ul role="list">
            <li>
              <p>Privacy &amp; data related issues</p>
              <a href="mailto:ConsumerPrivacy@health100.com">ConsumerPrivacy@health100.com</a>
            </li>
            <li>
              <p>General support</p>
              <a href="mailto:CustomerCare@health100.com">CustomerCare@health100.com</a>
            </li>
            <li>
              <p>App support</p>
              <a href="mailto:app.support@health100.com">app.support@health100.com</a>
            </li>
          </ul>
        }
      />
    </>
  );
};

export default Health100Footer;
